import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Modal,
  Form,
  Button,
  Input,
  DatePicker,
  Select,
  message,
  InputNumber,
  Row,
  Col,
  Tabs,
  Space,
  Tag,
} from "antd";
import {
  FileProtectOutlined,
  UserOutlined,
  BankOutlined,
  FileTextOutlined,
  CalendarOutlined,
  DollarOutlined,
  TeamOutlined,
  TagOutlined,
  PhoneOutlined,
  FileAddOutlined,
  NumberOutlined,
  PlusOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import locale from "antd/es/date-picker/locale/en_US";
import dayjs from "dayjs";
import { casesService } from "../../services/api.service";
import axiosInstance from "../../utils/axios.config";
import InitialPaymentModal from "../payments/InitialPaymentModal";
import DocumentUpload from "../documents/DocumentUpload";
import AddOpponentModal from "../opponents/AddOpponentModal";
import EditOpponentModal from "../opponents/EditOpponentModal";
import AddClientModal from "../clients/AddClientModal";
import Swal from "sweetalert2";

import logger from "../../utils/logger";

const { TextArea } = Input;
const { Option } = Select;

const AddCaseModal = ({
  visible,
  onClose,
  onSuccess,
  initialClientData = null,
  initialOpponentData = null,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [generatingNumber, setGeneratingNumber] = useState(false);
  const [clients, setClients] = useState([]);
  const [showClientsList, setShowClientsList] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedClient, setSelectedClient] = useState(null);
  const searchRef = useRef(null);
  const [showAddPaymentModal, setShowAddPaymentModal] = useState(false);
  const [newCaseData, setNewCaseData] = useState(null);
  const [paymentData, setPaymentData] = useState(null);
  const [tempPaymentData, setTempPaymentData] = useState(null);
  const [tempFormData, setTempFormData] = useState(null);
  const [activeTab, setActiveTab] = useState("1");
  const [caseCreatedId, setCaseCreatedId] = useState(null);
  const [showOpponentModal, setShowOpponentModal] = useState(false);
  const [opponents, setOpponents] = useState([]);
  const [tempCaseId, setTempCaseId] = useState(null);
  const [showEditOpponentModal, setShowEditOpponentModal] = useState(false);
  const [editingOpponent, setEditingOpponent] = useState(null);
  const [newOpponentName, setNewOpponentName] = useState("");
  const [opponentsList, setOpponentsList] = useState([]);
  const [showOpponentsList, setShowOpponentsList] = useState(false);
  const [opponentSearchTerm, setOpponentSearchTerm] = useState("");
  const opponentSearchRef = useRef(null);
  const [showAddClientModal, setShowAddClientModal] = useState(false);
  const [pendingClientName, setPendingClientName] = useState("");
  const [showInitialPaymentModal, setShowInitialPaymentModal] = useState(false);

  // Generate Case Number
  const generateCaseNumber = async () => {
    try {
      setGeneratingNumber(true);
      logger.debug("Starting to generate case number...");
      const currentYear = new Date().getFullYear();
      const cases = await casesService.getCases();
      logger.debug(`Fetched cases: ${cases.length}`);

      // فلترة القضايا للسنة الحالية واستخراج الأرقام
      const yearCases = cases
        .filter((c) => c.case_number?.startsWith(currentYear.toString()))
        .map((c) => {
          const numberPart = c.case_number.split("/")[1];
          return parseInt(numberPart, 10);
        })
        .filter((num) => !isNaN(num)); // إزالة الأرقام غير الصحيحة

      // إيجاد أكبر رقم موجود
      const maxNumber = yearCases.length > 0 ? Math.max(...yearCases) : 0;
      const nextNumber = (maxNumber + 1).toString().padStart(3, "0");
      const newCaseNumber = `${currentYear}/${nextNumber}`;

      logger.debug("Generated case number:", {
        newCaseNumber,
        maxNumber
      });
      logger.debug("Setting case number in form:", newCaseNumber);
      form.setFieldsValue({ case_number: newCaseNumber });

      // تأكد من أن القيمة تم تعيينها
      setTimeout(() => {
        const currentValue = form.getFieldValue("case_number");
        logger.debug("Case number after setting:", currentValue);
        if (!currentValue) {
          logger.warn("Case number not set, retrying...");
          form.setFieldsValue({ case_number: newCaseNumber });
        }
        setGeneratingNumber(false);
      }, 100);
    } catch (error) {
      logger.error("Error generating case number:", error);
      // في حالة الخطأ، استخدم timestamp لضمان التفرد
      const timestamp = Date.now().toString().slice(-3);
      const fallbackNumber = `${new Date().getFullYear()}/${timestamp}`;
      logger.info("Using fallback number:", fallbackNumber);
      form.setFieldsValue({ case_number: fallbackNumber });
      setGeneratingNumber(false);
    }
  };

  useEffect(() => {
    if (visible && form) {
      logger.debug("Modal is visible, generating case number...");
      // تأخير قصير للتأكد من أن النموذج جاهز
      setTimeout(() => {
        generateCaseNumber();
        // إعادة تعيين قائمة الخصوم والتحقق
        setOpponents([]);
        setNewOpponentName("");
        setOpponentSearchTerm("");
        setOpponentsList([]);
        setShowOpponentsList(false);
        form.setFieldsValue({ opponents_validator: "" });
        // لا نستدعي التحقق هنا لتجنب عرض رسالة الخطأ مبكرًا
      }, 100);
    }
  }, [visible, form]);

  // Set initial client data if provided
  useEffect(() => {
    if (initialClientData) {
      setSelectedClient(initialClientData);
      form.setFieldsValue({
        client_name: initialClientData.name,
        client_id: initialClientData.id,
        clientPhone: initialClientData.phone || "",
      });
      setSearchTerm(initialClientData.name);
    }
  }, [initialClientData, form]);

  // Set initial opponent data if provided
  useEffect(() => {
    logger.debug(
      "Received initialOpponentData in AddCaseModal:",
      initialOpponentData
    );
    if (initialOpponentData) {
      form.setFieldsValue({
        opponent_name: initialOpponentData.name,
        opponent_id: initialOpponentData.id,
        opponent_phone: initialOpponentData.phone || "",
        opponent_type:
          initialOpponentData.opponent_type || initialOpponentData.type,
        opponent_address: initialOpponentData.address || "",
        opponent_lawyer: initialOpponentData.lawyer_name || "",
        opponent_lawyer_phone: initialOpponentData.lawyer_phone || "",
        opponent_lawyer_office: initialOpponentData.lawyer_office || "",
      });
      // تحديث opponentSearchTerm لعرض اسم الخصم
      setOpponentSearchTerm(initialOpponentData.name);
      // إضافة الخصم إلى قائمة الخصوم
      setOpponents([initialOpponentData]);
    }
  }, [initialOpponentData, form]);

  // Debounce function
  const debounce = (func, wait) => {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), wait);
    };
  };

  // Search clients function
  const searchClients = async (term) => {
    if (!term) {
      setClients([]);
      setShowClientsList(false);
      return;
    }

    try {
      const response = await axiosInstance.get(`/clients/search?term=${term}`);
      setClients(response.data.data || []);
      setShowClientsList(true);
    } catch (error) {
      logger.error("Error searching clients:", error);
      setClients([]);
    }
  };

  // Search opponents function
  const searchOpponents = async (term) => {
    if (!term) {
      setOpponentsList([]);
      setShowOpponentsList(false);
      return;
    }

    try {
      // استخدام معلمة البحث الصحيحة
      const response = await axiosInstance.get(`/opponents?term=${term}`);
      setOpponentsList(response.data.data || []);
      setShowOpponentsList(true);
    } catch (error) {
      logger.error("Error searching opponents:", error);
      setOpponentsList([]);
    }
  };

  // Debounced search
  const debouncedSearch = useCallback(
    debounce((searchTerm) => searchClients(searchTerm), 300),
    []
  );

  // Debounced search for opponents
  const debouncedOpponentSearch = useCallback(
    debounce((searchTerm) => searchOpponents(searchTerm), 300),
    []
  );

  // Handle client search
  const handleClientSearch = (e) => {
    const term = e.target.value;
    setSearchTerm(term);
    if (!selectedClient) {
      form.setFieldsValue({ client_name: term });
    }
    debouncedSearch(term);

    // إذا كان هناك نص مدخل ولا يوجد عميل محدد،
    // ولم يتم فتح نموذج إضافة العميل،
    // وقائمة العملاء فارغة،
    // سنقوم بإضافة العميل الجديد عند فقدان التركيز
    if (
      term &&
      !selectedClient &&
      !showAddClientModal &&
      clients.length === 0
    ) {
      // تخزين النص المدخل للاستخدام لاحقاً
      setPendingClientName(term);
    }
  };

  // Handle client selection
  const handleClientSelect = (client) => {
    setSelectedClient(client);
    form.setFieldsValue({
      client_name: client.name,
      client_id: client.id,
      phone: client.phone || "", // تعيين رقم الهاتف تلقائياً
    });
    setShowClientsList(false);
    setSearchTerm(client.name);
  };

  // Handle client addition
  const handleClientAdded = (newClient) => {
    // استخراج معرف العميل الحقيقي من الاستجابة
    const clientId = newClient.id || newClient._id || newClient.data?.id;
    if (!clientId) {
      logger.error("Client ID not found in database response!");
      message.error("حدث خطأ في الحصول على معرف العميل");
      return;
    }

    // تحديث العميل المحدد بالبيانات الجديدة
    const client = {
      id: clientId,
      name: newClient.name || newClient.data?.name,
      phone: newClient.phone || newClient.data?.phone || "",
      email: newClient.email || newClient.data?.email || "",
      address: newClient.address || newClient.data?.address || "",
      national_id: newClient.national_id || newClient.data?.national_id || "",
      client_type: newClient.client_type || newClient.data?.client_type || "",
    };

    // تعيين العميل الجديد كعميل محدد
    setSelectedClient(client);
    form.setFieldsValue({
      client_name: client.name,
      client_id: client.id,
      phone: client.phone || "",
    });
    setSearchTerm(client.name);

    // إغلاق النافذة فوراً بعد الإضافة
    setShowAddClientModal(false);
    // مسح قائمة البحث
    setShowClientsList(false);

    message.success("تم إضافة العميل بنجاح");
  };

  // Handle client input blur
  const handleClientInputBlur = () => {
    // إذا كان هناك اسم عميل معلق ولم يتم اختيار عميل
    if (pendingClientName && !selectedClient && clients.length === 0) {
      // فتح نموذج إضافة العميل مع الاسم المدخل مسبقاً
      setShowAddClientModal(true);
    }
  };

  // Handle initial payment
  const handleInitialPayment = () => {
    // الحصول على بيانات النموذج الحالية
    const formValues = form.getFieldsValue();

    // إنشاء بيانات مؤقتة للقضية
    const tempCaseData = {
      title: formValues.title || "",
      case_number: formValues.case_number || "",
      agreed_fees: formValues.agreed_fees || 0,
      client_name: formValues.client_name || "",
      client_id: formValues.client_id || null,
      // لا نرسل معرف القضية لأنه لم يتم إنشاؤه بعد
    };

    // فتح نموذج إضافة الدفعة
    setShowInitialPaymentModal(true);
  };

  // Handle payment added
  const handlePaymentAdded = (paymentData) => {
    // حفظ بيانات الدفعة المؤقتة
    setTempPaymentData(paymentData);
    message.success(
      "تم حفظ بيانات الدفعة الأولية، سيتم إضافتها بعد إنشاء القضية"
    );
  };

  // Handle opponent search
  const handleOpponentSearch = (e) => {
    const term = e.target.value;
    setOpponentSearchTerm(term);
    debouncedOpponentSearch(term);
  };

  // Handle opponent selection
  const handleOpponentSelect = (opponent) => {
    // تحديث حالة الخصم المحدد
    const selectedOpponent = {
      id: opponent.id,
      name: opponent.name?.trim(),
      phone: opponent.phone || "",
      status: opponent.status || "active",
    };

    // تحديث قائمة الخصوم
    setOpponents([selectedOpponent]);

    // تحديث النموذج بالبيانات الجديدة بنفس طريقة handleClientSelect
    form.setFieldsValue({
      opponent_name: selectedOpponent.name,
      opponent_id: selectedOpponent.id, // تعيين معرف الخصم بشكل صريح
      opponent_phone: selectedOpponent.phone,
    });

    // تسجيل للتحقق من تعيين معرف الخصم
    logger.debug("Opponent ID set in form:", selectedOpponent.id);

    // تحديث واجهة المستخدم
    setShowOpponentsList(false);
    setOpponentSearchTerm(selectedOpponent.name);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowClientsList(false);
      }
      if (
        opponentSearchRef.current &&
        !opponentSearchRef.current.contains(event.target)
      ) {
        setShowOpponentsList(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // دالة لمعالجة تعديل خصم
  const handleOpponentUpdated = (updatedOpponent) => {
    const updatedOpponents = opponents.map((opponent) =>
      opponent.id === updatedOpponent.id ? updatedOpponent : opponent
    );
    setOpponents(updatedOpponents);
    // تحديث قيمة الحقل المخفي لتفعيل التحقق
    form.setFieldsValue({
      opponents_validator: updatedOpponents.length > 0 ? "valid" : "",
    });
    // إعادة التحقق من صحة النموذج
    setTimeout(() => form.validateFields(["opponents_validator"]), 100);
    setShowEditOpponentModal(false);
    setEditingOpponent(null);
  };

  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      logger.debug("Form values:", values);

      // التحقق من وجود خصم واحد على الأقل
      let finalOpponents = [...opponents];

      // التحقق من وجود خصم واحد على الأقل
      if (
        finalOpponents.length === 0 &&
        !values.opponent_name &&
        !values.opponent_id
      ) {
        message.error("يجب إضافة خصم واحد على الأقل");
        setLoading(false);
        return;
      }

      // طباعة قيمة معرف الخصم للتأكد
      logger.debug("Opponent ID from form:", values.opponent_id);

      // تحديد معرف الخصم الرئيسي (إما من قائمة الخصوم أو من اسم الخصم المدخل)
      let mainOpponent = null;

      if (finalOpponents.length > 0) {
        // البحث عن خصم بمعرف صحيح
        mainOpponent = finalOpponents.find((op) => {
          logger.debug("Checking opponent:", op);
          logger.debug("Opponent ID type:", typeof op.id);
          logger.debug("Opponent ID value:", op.id);
          return (
            op.id &&
            (typeof op.id === "number" ||
              (typeof op.id === "string" && !isNaN(parseInt(op.id, 10))))
          );
        });
      } else if (values.opponent_name && values.opponent_name.trim()) {
        // إذا لم يتم اختيار خصم من القائمة ولكن تم إدخال اسم، إنشاء خصم افتراضي
        mainOpponent = {
          name: values.opponent_name.trim(),
          id: null, // سيتم إنشاؤه في الخادم
        };
      }

      // إذا كان المعرف نصياً، نحوله إلى رقم
      const mainOpponentId = mainOpponent
        ? mainOpponent.id !== null
          ? typeof mainOpponent.id === "string"
            ? parseInt(mainOpponent.id, 10)
            : mainOpponent.id
          : null // سيتم إنشاؤه في الخادم
        : null;

      // التحقق من العميل المحدد
      const selectedClientId =
        values.client_id || (selectedClient ? selectedClient.id : null);
      logger.debug("Selected Client ID:", selectedClientId);

      // لم نعد نحتاج للتحقق من معرف الخصم، حيث يمكن إنشاؤه في الخادم
      logger.debug("Final opponent data:", mainOpponent);

      logger.debug("Selected opponent ID:", mainOpponentId);

      logger.debug("Final opponents before case creation:", finalOpponents);
      logger.debug("Main opponent selected:", mainOpponent);
      logger.debug("Main opponent ID to be used:", mainOpponentId);

      const formData = {
        ...values,
        agreed_fees: values.agreed_fees != null && values.agreed_fees !== "" 
          ? (() => {
              // إزالة الفاصلة التي يضيفها formatter في InputNumber
              const cleanValue = typeof values.agreed_fees === 'string' 
                ? values.agreed_fees.replace(/,/g, '') 
                : values.agreed_fees;
              return parseFloat(cleanValue);
            })()
          : null,
        case_number: values.case_number?.trim(),
        title: values.title?.trim(),
        client_name: values.client_name?.trim(),
        client_id: selectedClientId, // استخدام معرف العميل المحدد
        opponent_name: mainOpponent
          ? mainOpponent.name
          : values.opponent_name && values.opponent_name.trim(),
        opponent_id: mainOpponentId, // استخدام معرف الخصم إذا كان موجوداً
        court_name: values.court_name?.trim(),
        case_type: values.case_type?.trim(),
        status: values.status || "جديدة",
        filing_date: values.filing_date
          ? values.filing_date.format("YYYY-MM-DD")
          : new Date().toISOString().split("T")[0],
        description: values.description?.trim(),
        notes: values.notes?.trim(),
        phone: values.phone?.trim(),
        court_case_number: values.court_case_number?.trim(),
        next_session_date: values.next_session_date
          ? values.next_session_date.format("YYYY-MM-DD")
          : null,
      };

      // سجل للتحقق من البيانات قبل الإرسال
      logger.debug("Main Opponent ID:", mainOpponentId);
      logger.debug("Client ID:", selectedClientId);
      logger.debug("Form Values:", values);
      logger.debug("Initial Agreed Fees:", values.agreed_fees);
      logger.debug("Processed Agreed Fees:", formData.agreed_fees);
      logger.debug("Final Submission Data:", formData);

      // إنشاء القضية
      let response = await casesService.createCase(formData);

      if (response && (response.id || response.data?.id)) {
        const caseId = response.id || response.data?.id;

        // عرض رسالة النجاح قبل أي شيء آخر
        await Swal.fire({
          title: "تم بنجاح!",
          text: "تم إضافة القضية بنجاح",
          icon: "success",
          showConfirmButton: true,
          confirmButtonText: "حسناً",
          confirmButtonColor: "#3085d6",
          timer: 3000,
          timerProgressBar: true,
          allowOutsideClick: false,
        });

        setCaseCreatedId(caseId);
        setActiveTab("2");

        const caseData = {
          ...response,
          title: formData.title,
          case_number: formData.case_number,
          agreed_fees: formData.agreed_fees,
        };

        const clientData = selectedClient || {
          id: formData.client_id,
          name: formData.client_name,
          phone: formData.phone,
        };

        setNewCaseData(caseData);
        setSelectedClient(clientData);

        // استدعاء onSuccess لتحديث القائمة
        if (typeof onSuccess === "function") {
          onSuccess(response);
        }

        // إذا كانت هناك دفعة أولية مؤقتة، قم بإرسالها الآن
        if (tempPaymentData) {
          try {
            // تحديث بيانات الدفعة مع معرف القضية الجديد
            const finalPaymentData = {
              ...tempPaymentData,
              case_id: caseId,
              client_id: formData.client_id,
              // لم تعد دفعة مؤقتة
              is_temp_payment: false,
              temp_case_data: null,
            };

            // إرسال الدفعة إلى الخادم
            await axiosInstance.post("/payments", finalPaymentData);
            message.success("تم إضافة الدفعة الأولية بنجاح");
          } catch (paymentError) {
            logger.error("Error adding initial payment:", paymentError);
            message.error("حدث خطأ في إضافة الدفعة الأولية");
          }
        }

        // تنظيف النموذج وإغلاق النافذة
        form.resetFields();
        setTempPaymentData(null);
        if (onClose && typeof onClose === "function") {
          onClose();
        }
      } else {
        throw new Error("Failed to create case");
      }
    } catch (error) {
      logger.error("Error adding case:", error);
      if (
        error.message?.includes("Duplicate entry") &&
        error.message?.includes("case_number")
      ) {
        const timestamp = Date.now().toString().slice(-4);
        const newCaseNumber = `${new Date().getFullYear()}/${timestamp}`;
        form.setFieldsValue({ case_number: newCaseNumber });
        message.warning(
          "تم تعديل رقم القضية تلقائياً لتجنب التكرار. الرجاء المحاولة مرة أخرى."
        );
      } else {
        await Swal.fire({
          title: "خطأ!",
          text: "حدث خطأ في إنشاء القضية",
          icon: "error",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // إعداد بيانات النموذج بالكامل

  return (
    <>
      <Modal
        title={
          <div className="text-xl font-bold text-right text-gray-800 mb-4">
            <FileProtectOutlined className="ml-2 text-primary" />
            إضافة قضية جديدة
          </div>
        }
        open={visible}
        onCancel={onClose}
        okText={activeTab === "1" ? "إضافة القضية" : "إغلاق"}
        cancelText="إلغاء"
        onOk={activeTab === "1" ? () => form.submit() : onClose}
        confirmLoading={loading}
        width={900}
        dir="rtl"
        className="custom-modal"
      >
        <Form
          dir="rtl"
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            filing_date: dayjs(),
            status: "جديدة",
            case_number: `${new Date().getFullYear()}/001`, // قيمة افتراضية
            opponent_id: null,
          }}
          className="px-4"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* رقم القضية */}
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="case_number"
                  label="رقم القضية الداخلي"
                  rules={[{ required: true, message: "رقم القضية مطلوب" }]}
                >
                  <Input.Group compact>
                    <Input
                      prefix={<NumberOutlined />}
                      placeholder="جاري توليد رقم القضية..."
                      style={{ width: "calc(100% - 40px)" }}
                      value={form.getFieldValue("case_number")}
                      onChange={(e) =>
                        form.setFieldsValue({ case_number: e.target.value })
                      }
                    />
                    <Button
                      type="default"
                      onClick={() => {
                        logger.debug("Manual regeneration clicked");
                        generateCaseNumber();
                      }}
                      style={{ width: "40px" }}
                      title="إعادة توليد رقم القضية"
                      loading={generatingNumber}
                    >
                      🔄
                    </Button>
                  </Input.Group>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="court_case_number"
                  label="رقم القضية في المحكمة"
                >
                  <Input
                    prefix={<NumberOutlined />}
                    placeholder="رقم القضية في المحكمة"
                  />
                </Form.Item>
              </Col>
            </Row>

            {/* عنوان القضية */}
            <Form.Item
              name="title"
              label="عنوان القضية"
              rules={[{ required: true, message: "الرجاء إدخال عنوان القضية" }]}
            >
              <Input
                prefix={<FileTextOutlined className="ml-2" />}
                placeholder="أدخل عنوان القضية"
              />
            </Form.Item>

            {/* العميل مع البحث */}
            <div className="relative" ref={searchRef}>
              <Form.Item
                name="client_name"
                label="اسم العميل"
                rules={[{ required: true, message: "الرجاء اختيار العميل" }]}
              >
                <div className="flex items-center space-x-2 space-x-reverse">
                  <Input
                    value={searchTerm}
                    onChange={handleClientSearch}
                    onBlur={handleClientInputBlur}
                    prefix={<UserOutlined className="ml-2" />}
                    placeholder="ابحث عن عميل..."
                    style={{ width: "calc(100% - 120px)" }}
                  />
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => setShowAddClientModal(true)}
                    title="إضافة عميل جديد"
                    className="px-3 py-1 text-sm"
                  >
                    إضافة عميل
                  </Button>
                </div>
              </Form.Item>

              {/* حقل مخفي لـ client_id */}
              <Form.Item name="client_id" hidden>
                <Input />
              </Form.Item>

              {/* حقل مخفي لـ opponent_id */}
              <Form.Item name="opponent_id" hidden>
                <Input />
              </Form.Item>

              {/* حقل مخفي لـ opponent_name */}
              <Form.Item name="opponent_name" hidden>
                <Input />
              </Form.Item>

              {/* قائمة العملاء */}
              {showClientsList && clients.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-auto">
                  {clients.map((client) => (
                    <div
                      key={client.id}
                      className="p-3 hover:bg-gray-50 cursor-pointer border-b last:border-b-0"
                      onClick={() => handleClientSelect(client)}
                    >
                      <div className="font-medium">{client.name}</div>
                      {client.phone && (
                        <div className="text-sm text-gray-500">
                          رقم الهاتف: {client.phone}
                        </div>
                      )}
                      {client.email && (
                        <div className="text-sm text-gray-500">
                          البريد: {client.email}
                        </div>
                      )}
                      {client.address && (
                        <div className="text-sm text-gray-500">
                          العنوان: {client.address}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* هاتف العميل */}
            <Form.Item name="phone" label="هاتف العميل">
              <Input
                prefix={<PhoneOutlined className="ml-2" />}
                placeholder="رقم هاتف العميل"
                disabled={!!selectedClient} // تعطيل الحقل إذا تم اختيار عميل
              />
            </Form.Item>

            {/* نوع القضية */}
            <Form.Item
              name="case_type"
              label="نوع القضية"
              rules={[{ required: true, message: "الرجاء اختيار نوع القضية" }]}
            >
              <Select placeholder="اختر نوع القضية">
                <Option value="مدني">مدني</Option>
                <Option value="جزائي">جزائي</Option>
                <Option value="تجاري">تجاري</Option>
                <Option value="عمالي">عمالي</Option>
                <Option value="أحوال شخصية">أحوال شخصية</Option>
              </Select>
            </Form.Item>

            {/* حقل الخصوم مع البحث */}
            <div className="relative" ref={opponentSearchRef}>
              <Form.Item
                label="الخصوم"
                name="opponent_name"
                rules={[
                  {
                    required: true,
                    message: "يجب إدخال اسم الخصم",
                  },
                ]}
              >
                <div className="flex items-center space-x-2 space-x-reverse">
                  <Input
                    value={opponentSearchTerm}
                    onChange={handleOpponentSearch}
                    prefix={<UserOutlined className="ml-2" />}
                    placeholder="ابحث عن اسم الخصم..."
                  />
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => setShowOpponentModal(true)}
                    title="إضافة خصم جديد"
                    className="px-3 py-1 text-sm"
                  >
                    إضافة خصم
                  </Button>
                </div>
              </Form.Item>

              {/* قائمة الخصوم */}
              {showOpponentsList && opponentsList.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-auto">
                  {opponentsList.map((opponent) => (
                    <div
                      key={opponent.id}
                      className="p-3 hover:bg-gray-50 cursor-pointer border-b last:border-b-0"
                      onClick={() => handleOpponentSelect(opponent)}
                    >
                      <div className="font-medium">{opponent.name}</div>
                      {opponent.phone && (
                        <div className="text-sm text-gray-500">
                          رقم الهاتف: {opponent.phone}
                        </div>
                      )}
                      {opponent.address && (
                        <div className="text-sm text-gray-500">
                          العنوان: {opponent.address}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* عرض الخصوم المضافة */}
              {opponents.length > 0 && (
                <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                  <div className="text-sm font-medium text-gray-700 mb-2">
                    الخصوم المضافة:
                  </div>
                  <div className="space-y-2">
                    {opponents.map((opponent, index) => (
                      <div
                        key={opponent.id || index}
                        className="flex items-center justify-between p-2 bg-white rounded border"
                      >
                        <div>
                          <span className="font-medium">{opponent.name}</span>
                          {opponent.phone && (
                            <span className="mr-3 text-sm text-gray-500">
                              ({opponent.phone})
                            </span>
                          )}
                        </div>
                        <div className="flex items-center space-x-1 space-x-reverse">
                          <Button
                            type="text"
                            size="small"
                            icon={<FileTextOutlined />}
                            onClick={() => {
                              setEditingOpponent(opponent);
                              setShowEditOpponentModal(true);
                            }}
                            title="تعديل بيانات الخصم"
                          />
                          <Button
                            type="text"
                            size="small"
                            icon={<DeleteOutlined />}
                            onClick={() => {
                              const newOpponents = opponents.filter(
                                (_, i) => i !== index
                              );
                              setOpponents(newOpponents);
                              // تحديث معرف الخصم عند الحذف
                              form.setFieldsValue({
                                opponent_id:
                                  newOpponents.length > 0
                                    ? newOpponents[0].id
                                    : null,
                                opponent_name:
                                  newOpponents.length > 0
                                    ? newOpponents[0].name
                                    : "",
                              });
                              // إعادة التحقق من صحة النموذج
                              setTimeout(
                                () => form.validateFields(["opponent_id"]),
                                100
                              );
                            }}
                            title="حذف الخصم"
                            danger
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <Form.Item
              name="court_name"
              label="المحكمة"
              rules={[{ required: true }]}
            >
              <Input prefix={<BankOutlined />} placeholder="اسم المحكمة" />
            </Form.Item>

            <Form.Item
              name="filing_date"
              label="تاريخ التسجيل"
              rules={[{ required: true }]}
            >
              <DatePicker
                locale={locale}
                format="YYYY-MM-DD"
                style={{ width: "100%" }}
                placeholder="اختر التاريخ"
              />
            </Form.Item>

            <Form.Item name="next_session_date" label="تاريخ الجلسة القادمة">
              <DatePicker
                locale={locale}
                format="YYYY/MM/DD"
                style={{ width: "100%" }}
                placeholder="اختر تاريخ الجلسة القادمة"
              />
            </Form.Item>

            <Form.Item name="status" label="حالة القضية">
              <Select>
                <Option value="جديدة">جديدة</Option>
                <Option value="جارية">جارية</Option>
                <Option value="معلقة">معلقة</Option>
                <Option value="مكتملة">مكتملة</Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="agreed_fees"
              label="قيمة الأتعاب"
              rules={[
                {
                  type: "number",
                  transform: (value) => (value ? parseFloat(value) : null),
                  message: "الرجاء إدخال قيمة صحيحة",
                },
              ]}
            >
              <div className="flex items-center space-x-2 space-x-reverse">
                <InputNumber
                  prefix={<DollarOutlined />}
                  placeholder="أدخل قيمة الأتعاب"
                  style={{ width: "100%" }}
                  precision={0}
                  step={1}
                  min={0}
                  max={999999999}
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
                />
                <Button
                  type="primary"
                  icon={<DollarOutlined />}
                  onClick={handleInitialPayment}
                  title="إضافة دفعة أولية"
                  className="px-3 py-1 text-sm"
                >
                  دفعة أولية
                </Button>
              </div>
            </Form.Item>

            <Form.Item
              name="description"
              label="تفاصيل القضية"
              className="md:col-span-2"
            >
              <TextArea rows={4} placeholder="أدخل تفاصيل القضية" />
            </Form.Item>

            <Form.Item name="notes" label="ملاحظات" className="md:col-span-2">
              <TextArea rows={3} placeholder="أدخل الملاحظات" />
            </Form.Item>
          </div>
        </Form>

        {/* عرض معلومات العميل المختار */}

        {selectedClient && (
          <div className="mt-4 bg-blue-50 p-4 rounded-lg" dir="rtl">
            <h4 className="font-medium text-blue-800 mb-2">
              معلومات العميل المختار:
            </h4>
            <div
              className="grid grid-cols-2 gap-4 text-sm text-blue-600"
              dir="rtl"
            >
              <p>الاسم: {selectedClient.name}</p>
              <p>الهاتف: {selectedClient.phone || "غير متوفر"} </p>
              {selectedClient.email && <p>البريد: {selectedClient.email}</p>}
              {selectedClient.address && (
                <p>العنوان: {selectedClient.address}</p>
              )}
            </div>
          </div>
        )}
      </Modal>

      {/* نموذج إضافة خصم */}
      <AddOpponentModal
        isOpen={showOpponentModal}
        onClose={() => setShowOpponentModal(false)}
        onOpponentAdded={(newOpponent) => {
          // استخراج معرف الخصم الحقيقي من الاستجابة
          const dbId = newOpponent.id || newOpponent._id;
          if (!dbId) {
            logger.error("لم يتم العثور على معرف الخصم من قاعدة البيانات!");
            return;
          }
          // إضافة الخصم الجديد إلى قائمة الخصوم مع معرف قاعدة البيانات فقط
          setOpponents((prevOpponents) => {
            // منع التكرار
            if (prevOpponents.some((op) => op.id === dbId))
              return prevOpponents;

            logger.debug("Adding opponent with DB ID:", dbId);

            // تأكد من أن جميع خصائص الخصم موجودة
            const opponent = {
              id: dbId,
              name: newOpponent.name,
              opponent_type: newOpponent.opponent_type || "individual",
              phone: newOpponent.phone || "",
              email: newOpponent.email || "",
              address: newOpponent.address || "",
              lawyer_name: newOpponent.lawyer_name || "",
              lawyer_phone: newOpponent.lawyer_phone || "",
              lawyer_office: newOpponent.lawyer_office || "",
              notes: newOpponent.notes || "",
              status: newOpponent.status || "active",
            };

            logger.debug("Formatted opponent object:", opponent);

            // تعيين معرف الخصم في النموذج مباشرة
            form.setFieldsValue({
              opponent_id: dbId,
            });

            const updatedOpponents = [...prevOpponents, opponent];
            return updatedOpponents;
          });
          // إغلاق النافذة فوراً بعد الإضافة
          setShowOpponentModal(false);
          // تعيين اسم الخصم في حقل البحث
          setOpponentSearchTerm(newOpponent.name);
          // مسح قائمة البحث
          setShowOpponentsList(false);
          // تحديث قيمة التحقق في النموذج
          form.setFieldsValue({ opponents_validator: "valid" });
          // تعيين قيمة حقل الخصم في النموذج
          form.setFieldsValue({ opponent_name: newOpponent.name });
        }}
        zIndex={1100}
        showSuccessMessage={false}
      />

      {/* نموذج تعديل خصم */}
      <EditOpponentModal
        isOpen={showEditOpponentModal}
        onClose={() => {
          setShowEditOpponentModal(false);
          setEditingOpponent(null);
        }}
        opponentId={editingOpponent?.id}
        onOpponentUpdated={handleOpponentUpdated}
      />

      {/* نموذج إضافة عميل */}
      <AddClientModal
        visible={showAddClientModal}
        onClose={() => {
          setShowAddClientModal(false);
          setPendingClientName("");
        }}
        onSuccess={handleClientAdded}
        initialName={pendingClientName}
      />

      {/* نموذج إضافة دفعة أولية */}
      <InitialPaymentModal
        visible={showInitialPaymentModal}
        onClose={() => setShowInitialPaymentModal(false)}
        onSave={handlePaymentAdded}
        clientData={selectedClient}
        caseData={{
          title: form.getFieldValue("title") || "",
          case_number: form.getFieldValue("case_number") || "",
          agreed_fees: form.getFieldValue("agreed_fees") || 0,
          client_name: form.getFieldValue("client_name") || "",
          client_id: form.getFieldValue("client_id") || null,
          // لا نرسل معرف القضية لأنه لم يتم إنشاؤه بعد
        }}
      />
    </>
  );
};

export default AddCaseModal;
